import { useEffect } from 'react';
import {
  SITE_NAME,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  TWITTER_HANDLE,
  absoluteUrl,
  type JsonLd,
} from '../config/seo';

export interface SeoInput {
  /** Page title without the brand suffix. Omit on the landing page. */
  title?: string;
  /** Meta description. Falls back to the site default. */
  description?: string;
  /** Canonical path, e.g. '/blog/foo'. Defaults to the current location. */
  path?: string;
  /** Social-card image (root-relative or absolute). Defaults to the cover. */
  image?: string;
  /** Open Graph type. 'article' for blog posts, else 'website'. */
  type?: 'website' | 'article';
  /** One or more JSON-LD objects to inject for this page. */
  jsonLd?: JsonLd | JsonLd[];
  /** Set true to emit noindex (e.g. 404). */
  noindex?: boolean;
}

const DEFAULT_TITLE = `${SITE_NAME} — cover the spread. spill the tea.`;

/** Find an existing head meta/link tag or create it, then set an attribute. */
function upsert(
  selector: string,
  create: () => HTMLElement,
): HTMLElement {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, key);
    return m;
  });
  el.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  const el = upsert('link[rel="canonical"]', () => {
    const l = document.createElement('link');
    l.setAttribute('rel', 'canonical');
    return l;
  });
  el.setAttribute('href', href);
}

/**
 * Manage the document head for the current route: title, description,
 * canonical, Open Graph + Twitter cards, robots, and JSON-LD.
 *
 * Singleton tags (description, canonical, og:*, twitter:*, robots) are UPSERTED
 * in place so the static baseline in `index.html` is overwritten rather than
 * duplicated — every route leaves exactly one of each. Per-page JSON-LD is
 * appended with `data-managed-seo` and removed on unmount, so it never leaks to
 * the next route (the static Organization/WebSite blocks are untagged and stay).
 *
 * Crawler-visibility caveat: these run in JS, so social scrapers and non-JS AI
 * crawlers see only the `index.html` baseline until routes are prerendered.
 */
export function useSeo(input: SeoInput): void {
  const {
    title,
    description = DEFAULT_DESCRIPTION,
    path,
    image = DEFAULT_OG_IMAGE,
    type = 'website',
    jsonLd,
    noindex = false,
  } = input;

  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';
  const shouldNoindex = noindex || import.meta.env.VITE_SHOP_MODE === 'sandbox';

  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : DEFAULT_TITLE;
    document.title = fullTitle;

    const canonical = absoluteUrl(path ?? window.location.pathname);
    const imageUrl = absoluteUrl(image);

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', shouldNoindex ? 'noindex,nofollow' : 'index,follow');
    upsertCanonical(canonical);

    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', imageUrl);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', imageUrl);
    if (TWITTER_HANDLE) {
      upsertMeta('name', 'twitter:site', TWITTER_HANDLE);
      upsertMeta('name', 'twitter:creator', TWITTER_HANDLE);
    }

    // Per-page JSON-LD: clear any left by a prior route, then add this page's.
    document.head
      .querySelectorAll('[data-managed-seo]')
      .forEach((el) => el.remove());

    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const block of blocks) {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-managed-seo', '');
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
      }
    }

    return () => {
      document.head
        .querySelectorAll('[data-managed-seo]')
        .forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, type, shouldNoindex, jsonLdKey]);
}
